import { useState } from 'react';

import { usePauseIntegrations, useResumeIntegrations } from '../hooks/use-mcp-connectors';

export interface PauseIntegrationsDialogProps {
  mode: 'pause' | 'resume';
  affectedCount: number;
  onClose: () => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 14. Dialog modal pause/resume da feature global.
 *
 * Backend marca todos os conectores com `pausedAt` (pause) ou limpa
 * (resume) + emite WS `integrations.feature-toggled` que invalida list +
 * customer-context (regra do silêncio em conversa quando feature off).
 */
export function PauseIntegrationsDialog({
  mode,
  affectedCount,
  onClose,
}: PauseIntegrationsDialogProps): JSX.Element {
  const pauseMutation = usePauseIntegrations();
  const resumeMutation = useResumeIntegrations();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isLoading = pauseMutation.isPending || resumeMutation.isPending;

  function handleConfirm(): void {
    setSubmitError(null);
    const mutation = mode === 'pause' ? pauseMutation : resumeMutation;
    mutation.mutate(undefined, {
      onSuccess: () => {
        onClose();
      },
      onError: (err: Error) => {
        setSubmitError(err.message);
      },
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface p-6">
        <h2 className="text-lg font-semibold text-text-primary">
          {mode === 'pause' ? 'Pausar integrações externas?' : 'Reativar integrações externas?'}
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          {mode === 'pause' ? (
            <>
              Conectores cadastrados continuam preservados, mas o contexto do cliente nas conversas
              deixa de mostrar dados externos. Você pode reativar a qualquer momento.
            </>
          ) : (
            <>
              A feature de integrações externas voltará a ficar ativa. O contexto do cliente nas
              conversas voltará a mostrar dados dos conectores cadastrados.
            </>
          )}
        </p>
        {affectedCount > 0 && (
          <p className="mt-2 text-xs text-text-secondary">
            Conectores afetados: <strong>{affectedCount}</strong>
          </p>
        )}

        {submitError !== null && (
          <div className="mt-4 rounded bg-red-50 p-3 text-xs text-red-900">
            <strong>Erro:</strong> {submitError}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {isLoading ? 'Aplicando...' : mode === 'pause' ? 'Pausar' : 'Reativar'}
          </button>
        </div>
      </div>
    </div>
  );
}
